import React, { useRef, useState } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, X, Image as ImageIcon } from "lucide-react";

/**
 * Reusable URL input + "Subir archivo" button + drag & drop zone.
 * Uploads to the configured endpoint (default: client media library).
 * For super-admin usage pass uploadEndpoint="/admin/upload" and uploadAxios={adminAxios}.
 *
 * Props:
 *   value            — current URL string
 *   onChange         — (newUrl: string) => void
 *   accept           — MIME filter (default: image + video + audio + pdf)
 *   placeholder      — input placeholder
 *   testIdPrefix     — base for data-testid attributes
 *   uploadEndpoint   — relative API path (default "/upload/media-library")
 *   uploadAxios      — axios instance to use (default: authAxios from AuthContext)
 *   compact          — reduce padding if true
 *   previewOnly      — if true, renders a big drag zone instead of input+button (used for logo/hero)
 */
export default function MediaUploadInput({
  value = "",
  onChange,
  accept = "image/*,video/*,audio/*,application/pdf",
  placeholder = "https://... o arrastra / sube un archivo",
  testIdPrefix = "media-upload",
  uploadEndpoint = "/upload/media-library",
  uploadAxios,
  compact = false,
  previewOnly = false,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const resolveAxios = () => uploadAxios || authAxios;

  const handleUpload = async (file) => {
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", file.name);
      const instance = resolveAxios();
      const url = uploadEndpoint.startsWith("http") ? uploadEndpoint : `${API}${uploadEndpoint}`;
      const { data } = await instance.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newUrl = data.file_url || data.url || "";
      if (!newUrl) throw new Error("El servidor no devolvió un URL");
      onChange(newUrl);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Error al subir");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const isImage = value && /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(value);

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-wrap`}>
      {previewOnly ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`cursor-pointer border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 transition ${
            dragOver
              ? "border-emerald-400 bg-emerald-500/10"
              : "border-white/15 hover:border-emerald-400/60 bg-white/5"
          } ${compact ? "py-4" : "py-8"}`}
          data-testid={`${testIdPrefix}-dropzone`}
        >
          {value && isImage ? (
            <>
              <img src={value} alt="preview" className="max-h-32 rounded" />
              <p className="text-xs text-slate-400">Click o arrastra para reemplazar</p>
            </>
          ) : (
            <>
              {uploading ? (
                <Loader2 className="w-7 h-7 text-emerald-400 animate-spin" />
              ) : (
                <ImageIcon className="w-7 h-7 text-slate-400" />
              )}
              <p className="text-sm text-slate-300">
                {uploading ? "Subiendo..." : "Arrastra una imagen aquí o haz click"}
              </p>
              <p className="text-xs text-slate-500">PNG, JPG, WEBP · max 50 MB</p>
            </>
          )}
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`flex gap-2 items-center rounded-md transition ${
            dragOver ? "ring-2 ring-emerald-400 ring-offset-1 ring-offset-transparent" : ""
          }`}
        >
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="flex-1"
            data-testid={`${testIdPrefix}-url`}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`${testIdPrefix}-btn`}
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Subiendo...</>
            ) : (
              <><Upload className="w-4 h-4 mr-1" /> Subir</>
            )}
          </Button>
          {value && (
            <Button
              type="button"
              variant="outline"
              onClick={() => onChange("")}
              title="Limpiar"
              data-testid={`${testIdPrefix}-clear`}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept={accept}
        onChange={(e) => handleUpload(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testIdPrefix}-file`}
      />
      {!previewOnly && value && isImage && (
        <img src={value} alt="preview" className="mt-1 max-h-24 rounded border" />
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}
